  import { Component, OnInit, NgZone, OnDestroy } from '@angular/core';
  import { MatDialog } from '@angular/material/dialog';
  import { Router, NavigationEnd } from '@angular/router';
  import { Auth, onAuthStateChanged } from '@angular/fire/auth';
  import { RegisterComponent } from 'src/app/views/auth/register/register.component';
  import { ServiceOfferDialogComponent } from 'src/app/views/auth/service-offer-dialog/service-offer-dialog.component';
  import { BusinessLoginComponent } from 'src/app/views/auth/business-login/business-login.component';
  import { filter } from 'rxjs/operators';
  import { Firestore, doc, getDoc, onSnapshot } from '@angular/fire/firestore';
  import { LoginComponent } from 'src/app/views/auth/login/login.component';
  import { CartService } from 'src/app/shared/services/cart.service';
  import { Observable, Subject } from 'rxjs';
  import { takeUntil } from 'rxjs/operators';
  import { NotificationService } from 'src/app/shared/services/notification.service';
  import { NotificationListDialogComponent } from 'src/app/components/notification-list-dialog/notification-list-dialog.component';
  import { RoleSelectionDialogComponent } from 'src/app/views/auth/role-selection-dialog/role-selection-dialog.component';
  import { environment } from 'src/app/environments/environment';
  import { ReferVendorDialogComponent } from 'src/app/components/refer-vendor-dialog/refer-vendor-dialog.component';
  import { AuthService } from 'src/app/shared/services/auth.service';
  import { UxserviceService } from 'src/app/shared/services/uxservice.service';

  @Component({
    selector: 'app-normaluser-layout',
    templateUrl: './normaluser-layout.component.html',
    styleUrls: ['./normaluser-layout.component.scss'],
  })
  export class NormaluserLayoutComponent implements OnInit, OnDestroy {
    private userSub: any = null;
    appVersion = environment.version;
    raiosoftUrl = 'https://raiosoft.com';
    // NAVBAR CONDITIONS
    isIconNavbar = false; // Condition 1
    isSearchNavbar = false; // Condition 2
    isFormPage = false; // Condition 3 (Registration/Setup forms)

    // DROPDOWN
    loggedMenuOpen = false;
    menuOpen = false;
    notificationOpen = false; // New dropdown state
    cartDropdownOpen = false;
    private destroy$ = new Subject<void>();
    private cartCloseTimer: any;

    // USER STATE
    currentuid: string | null = null;
    currentusername: string | null = null;
    currentuserImage: string | null = null;

    // NAVIGATION
    activeNav: string = '/';

    // ROLE FLAGS
    isRealVendor = false;
    isBusinessUser = false;
    isCustomer = false;
    isVisiting = true;
    accountStatus: string = 'active';
    parentAccountStatus: string = 'active';
    currentUserData: any = null;

    cartCount$: Observable<number>;
    unreadNotificationCount$: Observable<number>;
    notifications$: Observable<any[]>;

    constructor(
      private dialog: MatDialog,
      private auth: Auth,
      private router: Router,
      private firestore: Firestore,
      public cartService: CartService,
      public notificationService: NotificationService,
      private ngZone: NgZone,
      private authService: AuthService,
      private uxservice: UxserviceService,
    ) {
      // Detect route + apply navbar logic
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          const url = event.url;

          // CONDITION 1 — SHOW ICON NAV
          this.isIconNavbar =
            url === '/' ||
            url.startsWith('/home') ||
            url.startsWith('/booking-garage') ||
            url.startsWith('/other-services');

          // CONDITION 2 — SHOW SEARCH BAR
          this.isSearchNavbar =
            url.startsWith('/category') ||
            url.startsWith('/booking-detail') ||
            url.startsWith('/search');

          // Active nav highlight
          if (url === '/' || url.startsWith('/home')) {
            this.activeNav = '/';
          } else if (url.startsWith('/booking-garage')) {
            this.activeNav = '/booking-garage';
          } else if (url.startsWith('/other-services')) {
            this.activeNav = '/other-services';
          } else {
            this.activeNav = '';
          }

          // CONDITION 3 — DETECT FORM PAGES (Register, Register-Business, Setup-Role)
          this.isFormPage =
            url.startsWith('/register') ||
            url.startsWith('/auth/register') ||
            url.startsWith('/register-business') ||
            url.startsWith('/auth/register-business') ||
            url.startsWith('/setup-role') ||
            url.startsWith('/auth/setup-role');
        });

      this.cartCount$ = this.cartService.getCartCount();
      this.unreadNotificationCount$ = this.notificationService.unreadCount$;
      this.notifications$ = this.notificationService.notifications$;

      // Handle automatic cart opening when an item is added
      this.cartService.itemAdded$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.cartDropdownOpen = true;
          this.startCartCloseTimer(3000); // Auto-close after 3 seconds
        });
    }

    ngOnInit() {
      onAuthStateChanged(this.auth, async (user) => {
        if (user) {
          this.currentuid = user.uid;
          this.currentusername =
            user.displayName ?? user.email?.split('@')[0] ?? null;

          const ref = doc(this.firestore, 'users-list', user.uid);

          // Use real-time listener to keep navbar in sync with role changes
          this.userSub = onSnapshot(ref, async (snap) => {
            if (snap.exists()) {
              const data: any = snap.data();
              this.currentUserData = data;
              const role = data.role || 'customer';
              const isIndividual = data.isIndividual !== false;

              this.isCustomer = (role === 'customer' && !isIndividual);
              this.isVisiting = (role === 'normaluser') || (role === 'customer' && isIndividual);
              this.isRealVendor = (role === 'business' || role === 'worker');
              this.accountStatus = data.accountStatus || 'active';

              if (role === 'worker' && data.parentUid) {
                try {
                  const parentSnap = await getDoc(doc(this.firestore, 'users-list', data.parentUid));
                  if (parentSnap.exists()) {
                    this.parentAccountStatus = parentSnap.data()?.['accountStatus'] || 'active';
                  }
                } catch (e) {
                  console.error('Error fetching parent account status in navbar:', e);
                }
              }

              if (data.photoURL) {
                this.currentuserImage = data.photoURL;
              } else if (user.photoURL) {
                this.currentuserImage = user.photoURL;
              } else {
                this.currentuserImage = null;
              }
            }
          });
        } else {
          // Cleanup listener on logout
          if (this.userSub) {
            this.userSub();
            this.userSub = null;
          }
          this.currentuid = null;
          this.currentusername = null;
          this.currentuserImage = null;

          this.isRealVendor = false;
          this.isBusinessUser = false;
          this.isCustomer = false;
          this.isVisiting = true;
          this.accountStatus = 'active';
          this.parentAccountStatus = 'active';
          this.currentUserData = null;
        }
      });

      document.addEventListener('click', () => {
        this.loggedMenuOpen = false;
        this.menuOpen = false;
        this.notificationOpen = false;
        this.cartDropdownOpen = false;
      });
    }

    // MENU
    toggleLoggedMenu(event: MouseEvent) {
      event.stopPropagation();
      this.loggedMenuOpen = !this.loggedMenuOpen;
      this.menuOpen = false;
      this.notificationOpen = false;
      this.cartDropdownOpen = false;
    }

    toggleMenu(event: MouseEvent) {
      event.stopPropagation();
      this.menuOpen = !this.menuOpen;
      this.loggedMenuOpen = false;
      this.notificationOpen = false;
      this.cartDropdownOpen = false;
    }

    toggleNotification(event: MouseEvent) {
      event.stopPropagation();
      this.notificationOpen = !this.notificationOpen;
      this.menuOpen = false;
      this.loggedMenuOpen = false;
      this.cartDropdownOpen = false;

      if (this.notificationOpen && this.currentuid) {
        this.notificationService.markAllAsRead(this.currentuid);
      }
    }

    toggleCartDropdown(event: MouseEvent) {
      event.stopPropagation();
      this.cartDropdownOpen = !this.cartDropdownOpen;
      this.menuOpen = false;
      this.loggedMenuOpen = false;
      this.notificationOpen = false;

      if (this.cartDropdownOpen) {
        this.startCartCloseTimer(5000); // Auto-close after 5 seconds if not hovered
      } else {
        if (this.cartCloseTimer) {
          clearTimeout(this.cartCloseTimer);
          this.cartCloseTimer = null;
        }
      }
    }

    startCartCloseTimer(delay: number) {
      if (this.cartCloseTimer) clearTimeout(this.cartCloseTimer);
      this.cartCloseTimer = setTimeout(() => {
        this.cartDropdownOpen = false;
        this.cartCloseTimer = null;
      }, delay);
    }

    onCartMouseEnter() {
      if (this.cartCloseTimer) {
        clearTimeout(this.cartCloseTimer);
        this.cartCloseTimer = null;
      }
    }

    onCartMouseLeave() {
      if (this.cartDropdownOpen) {
        this.startCartCloseTimer(1000); // Close after 1 second of leaving
      }
    }

    markAsRead(notification: any) {
      if (!this.currentuid) return;
      this.notificationService.markAsRead(this.currentuid, notification.id);
    }

    get isAccountDisabled(): boolean {
      if (this.accountStatus === 'disabled') return true;
      if (this.currentUserData?.role === 'worker' && this.parentAccountStatus === 'disabled') return true;
      return false;
    }

    // NAV ACTIONS
    async goToVendorDashboard(type?: string) {
      if (type === 'real') {
        if (this.isAccountDisabled) {
          const msg = (this.currentUserData?.role === 'worker' && this.parentAccountStatus === 'disabled')
            ? 'Your company account has been disabled by administration. Access to the Vendor Dashboard is restricted. Please contact support.'
            : 'Your vendor account has been disabled by administration. Access to the Vendor Dashboard is restricted. Please contact support.';
          this.uxservice.showMessage(msg, 'Account Disabled', 'error');
          return;
        }

        // Live verification in case real-time listener snapshot is pending
        if (this.currentuid) {
          try {
            const userSnap = await getDoc(doc(this.firestore, 'users-list', this.currentuid));
            if (userSnap.exists()) {
              const uData: any = userSnap.data();
              if (uData?.accountStatus === 'disabled') {
                this.accountStatus = 'disabled';
                this.uxservice.showMessage(
                  'Your vendor account has been disabled by administration. Access to the Vendor Dashboard is restricted. Please contact support.',
                  'Account Disabled',
                  'error'
                );
                return;
              }
              if (uData?.role === 'worker' && uData?.parentUid) {
                const parentSnap = await getDoc(doc(this.firestore, 'users-list', uData.parentUid));
                if (parentSnap.exists() && parentSnap.data()?.['accountStatus'] === 'disabled') {
                  this.parentAccountStatus = 'disabled';
                  this.uxservice.showMessage(
                    'Your company account has been disabled by administration. Access to the Vendor Dashboard is restricted. Please contact support.',
                    'Account Disabled',
                    'error'
                  );
                  return;
                }
              }
            }
          } catch (e) {
            console.error('Error verifying vendor status in goToVendorDashboard:', e);
          }
        }

        this.router.navigate(['/business']);
      } else if (type === 'dummy') {
        this.router.navigate(['/dummy']);
      }
    }

    goToProfile() {
      this.router.navigate(['/profile']);
    }

    goToBookings() {
      this.router.navigate(['/booking-orders']);
    }

    logout() {
      this.authService.userSignOut();
    }

    openReferVendorDialog() {
      this.loggedMenuOpen = false;
      this.dialog.open(ReferVendorDialogComponent, {
        width: '720px',
        maxWidth: '95vw',
        panelClass: 'custom-dialog-container',
        backdropClass: 'blurred-backdrop',
      });
    }

    // DIALOGS
    Login(defaultTab: 'login' | 'register' = 'login') {
      // Navigate to login page instead of dialog
      this.router.navigate(['/auth/login']);
      // Actually, based on AppRoutingModule, there is no explicit 'login' route yet.
      // But Step 1 (Register) added 'register' to 'auth'.
      // I should check if I need to add 'login' to AppRoutingModule first.
      // In original AppRoutingModule (Step 136), there IS NO explicit 'login' route.
      // Wait, the user asked to "convert the login component that is make it a page".
      // I need to add the route first.
      // I will do that in the next step. For now, let's assume the route will be '/login' or '/auth/login'.
      // Given 'register' was added as '/register' (lazy loaded under blank path? No, 'register' was added to children of NormalUserLayout).
      // So it should be '/login'.
      this.router.navigate(['/login']);
    }

    SignUp(defaultTab: 'register' | 'login' = 'register') {
      this.router.navigate(['/register']);
    }

    openServiceOfferDialog() {
      // Bypass business type selection \u2014 only Heavy Equipment Rental is offered
      const selection = 'rental';
      if (this.currentuid) {
        this.router.navigate(['/setup-role'], {
          queryParams: {
            type: 'vendor',
            businessType: selection
          }
        });
      } else {
        // Unauthenticated user \u2014 go to registration first
        this.router.navigate(['/register'], {
          queryParams: {
            returnUrl: `/setup-role?type=vendor&businessType=${selection}`
          }
        });
      }
    }

    openBusinessLoginPopup() {
      this.dialog.open(BusinessLoginComponent, {
        disableClose: false,
        panelClass: 'loginbox',
        backdropClass: 'blurred-backdrop',
      });
    }

    openCreatemyaccountbox() {
      this.router.navigate(['/register']);
    }

    openCreatebusinessuser() {
      this.router.navigate(['/register-business']);
    }

    goToCart() {
      this.router.navigate(['/cart']);
    }

    goToFAQ() {
      // Navigate to FAQ page when implemented
      // this.router.navigate(['/faq']);
    }

    openSwitchRoleDialog() {
      this.loggedMenuOpen = false;
      const dialogRef = this.dialog.open(RoleSelectionDialogComponent, {
        disableClose: true,
        panelClass: 'role-selection-panel',
        width: '560px',
        maxWidth: '95vw',
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result && result !== 'skip') {
          // Role changed — reload to update navbar state
          window.location.reload();
        }
      });
    }

    handleNotificationClick(notification: any) {
      this.markAsRead(notification);
      this.notificationOpen = false; // Close dropdown
      this.ngZone.run(() => {
        // Use the notification's own link field to determine the correct route.
        // This ensures a vendor who booked as a customer (link: '/booking-orders')
        // is taken to the customer flow, not the vendor booking-details flow.
        const notifLink: string = notification.link || '';

        if (notifLink.startsWith('/booking-orders')) {
          // Customer-side notification — go to customer order flow
          if (notification.bookingId) {
            this.router.navigate(['/booking-orders', notification.bookingId]);
          } else {
            this.router.navigate(['/booking-orders']);
          }
        } else if (this.isRealVendor) {
          // Vendor-side notification — go to vendor booking-details flow
          if (notification.bookingId) {
            this.router.navigate([
              '/business/booking/booking-details',
              notification.bookingId,
            ]);
          } else {
            this.router.navigate(['/business/booking']);
          }
        } else {
          // Regular customer with no specific link
          if (notification.bookingId) {
            this.router.navigate(['/booking-orders', notification.bookingId]);
          } else {
            this.router.navigate(['/booking-orders']);
          }
        }
      });
    }


    viewAllNotifications() {
      this.notificationOpen = false; // Close dropdown
      this.router.navigate(['/notifications']);
    }

    clearAllNotifications() {
      if (this.currentuid) {
        if (confirm('Are you sure you want to clear all notifications?')) {
          this.notificationService.clearAllNotifications(this.currentuid);
        }
      }
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
      if (this.userSub) {
        this.userSub();
      }
      if (this.cartCloseTimer) {
        clearTimeout(this.cartCloseTimer);
      }
    }
  }
